import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table,
  InputNumber,
  Select,
  Button,
  Breadcrumb,
  Space,
  Form,
  Row,
  Col,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';

import { apiRequest } from '../../../../service/apiRequest';
import { Wood } from '../../../../types/Wood';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { HPO } from '../../../../types/HPO';

const { Option } = Select;

// Type for each row
type WoodRow = {
  key: string;
  woodId: number | null;
  wood_type_id: number | null;
  wood_type_name: string;
  pbg_cft: number;
  rate: number;
  widths: {
    [width: number]: {
      accepted: number;
      rejection: number;
      keeda: number;
    };
  };
};

const woodWidths = Array.from({ length: 18 }, (_, i) => i + 3); // 3" to 20"

const PartyNapi: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [wooddata, setWoodData] = useState<Wood[]>([]);
  const [billdata, setBillData] = useState<HPO | null>(null);
  const [dataSource, setDataSource] = useState<WoodRow[]>([]);

  // Fetch woods
  const fetchWood = async () => {
    try {
      const data = await apiRequest<Wood[]>({ method: 'get', url: 'woods' });
      setWoodData(data);
    } catch (error) {
      showError('Failed to fetch Wood');
      console.error(error);
    }
  };

  // Fetch PO bill
  const fetchBill = async (id: string) => {
    try {
      const params = new URLSearchParams({ id });
      const data = await apiRequest<HPO>({
        method: 'get',
        url: `get-po-bill?${params.toString()}`,
      });
      setBillData(data);
    } catch (error) {
      showError('Failed to fetch PO Bill');
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) fetchBill(id);
    fetchWood();
  }, [id]);

  // Initialize rows
  useEffect(() => {
    if (billdata?.po_details?.length) {
      const rows: WoodRow[] = billdata.po_details.map((item, index) => {
        const hasSelfNapi = Boolean(
          item.self_widths && Object.keys(item.self_widths).length > 0
        );
        return {
          key: (index + 1).toString(),
          woodId: item.wood_id ?? null,
          wood_type_id: item.wood_type_id ?? null,
          wood_type_name: item.wood_type_name ?? '',
          pbg_cft: item.pbg_cft ?? 0,
          rate: item.pbg_rate ?? 0,
          widths: woodWidths.reduce(
            (acc, w) => ({
              ...acc,
              [w]: {
                accepted: hasSelfNapi ? (item.self_widths?.[w]?.accepted ?? 0) : 0,
                rejection: hasSelfNapi ? (item.self_widths?.[w]?.rejection ?? 0) : 0,
                keeda: hasSelfNapi ? (item.self_widths?.[w]?.keeda ?? 0) : 0,
              },
            }),
            {}
          ),
        };
      });
      setDataSource(rows);
    }
  }, [billdata]);

  // Handlers
  const handleWoodChange = (value: number | null, key: string) => {
    setDataSource(prev =>
      prev.map(row => (row.key === key ? { ...row, woodId: value } : row))
    );
  };

  const handleWidthChange = (
    key: string,
    width: number,
    field: 'accepted' | 'rejection' | 'keeda',
    value: number | null
  ) => {
    setDataSource(prev =>
      prev.map(row =>
        row.key === key
          ? {
            ...row,
            widths: {
              ...row.widths,
              [width]: { ...row.widths[width], [field]: value ?? 0 },
            },
          }
          : row
      )
    );
  };
  const FIXED_RIGHT_WIDTH = 460;
  // Scroll input into view
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const container = tableRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();

    const visibleRight =
      containerRect.right - FIXED_RIGHT_WIDTH;

    // 👉 Scroll RIGHT
    if (inputRect.right > visibleRight) {
      container.scrollLeft += inputRect.right - visibleRight + 16;
    }

    // 👉 Scroll LEFT
    if (inputRect.left < containerRect.left) {
      container.scrollLeft -= containerRect.left - inputRect.left + 16;
    }
  };

  // Utility: total pieces
  const calculateTotalPcs = (widths: WoodRow['widths']) =>
    Object.values(widths).reduce(
      (sum, w) => sum + w.accepted + w.rejection + w.keeda,
      0
    );

  // Utility: calculate CFT
  const calculateCFT = (
    woodId: number | null,
    widths: WoodRow['widths']
  ) => {
    if (!woodId) return 0;
    const wood = wooddata.find(w => w.wood_id === woodId);
    if (!wood) return 0;

    const length = Number(wood.wood_length ?? 0);
    const thickness = Number(wood.wood_thickness ?? 0);

    const widthQtyTotal = Object.entries(widths).reduce(
      (sum, [w, qty]) => sum + Number(w) * (qty.accepted + qty.rejection + qty.keeda),
      0
    );

    const cft = (widthQtyTotal * ((length / 12) * thickness)) / 144;
    return parseFloat(cft.toFixed(4));
  };

  const calculateAmount = (cft: number, rate: number) =>
    parseFloat((cft * rate).toFixed(4));

  // Submit
  const handleSubmit = async () => {
    const invalidWood = dataSource.find(row => !row.woodId);
    if (invalidWood) return showError('Please select wood for all rows');

    const invalidWidths = dataSource.find(row =>
      Object.values(row.widths).every(w => w.accepted + w.rejection + w.keeda === 0)
    );
    if (invalidWidths)
      return showError('Each row must have at least one width quantity');

    const payload = {
      po_id: id,
      woodDetails: dataSource.map(row => ({
        woodId: row.woodId,
        wood_type_id: row.wood_type_id,
        rate: row.rate,
        widths: row.widths,
      })),
    };

    try {
      await apiRequest({ method: 'post', url: 'insert-self-napi', data: payload });
      showSuccess('Data submitted successfully!');
      navigate(`/purchase-order?type=timber`);
    } catch (error) {
      showError('Failed to submit data');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Description',
      key: 'woodSize',
      fixed: 'left' as const,
      width: 180,
      render: (_: any, record: WoodRow) => (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: 'auto auto repeat(3, 24px)',
            rowGap: 6,
          }}
        >
          <strong>{record.wood_type_name}</strong>

          <Select
            value={record.woodId}
            onChange={val => handleWoodChange(val, record.key)}
            placeholder="Select wood"
            options={wooddata.map(w => ({
              label: w.wood_name,
              value: w.wood_id,
            }))}
            allowClear
          />

          <span>Accepted</span>
          <span>Rejected</span>
          <span>Keeda</span>
        </div>
      ),
    },
    ...woodWidths.map(width => ({
      title: `${width}"`,
      key: `width-${width}`,
      width: 110,
      render: (_: any, record: WoodRow) => (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: 'auto auto repeat(3, 24px)',
            rowGap: 6,
            marginTop: 32,
          }}
        >
          {/* EMPTY rows to align with Description */}
          <div />
          <div />

          <InputNumber
            min={0}
            value={record.widths[width].accepted}
            onChange={val =>
              handleWidthChange(record.key, width, 'accepted', val)
            }
            onFocus={handleInputFocus}
          />

          <InputNumber
            min={0}
            value={record.widths[width].rejection}
            onChange={val =>
              handleWidthChange(record.key, width, 'rejection', val)
            }
            onFocus={handleInputFocus}
          />

          <InputNumber
            min={0}
            value={record.widths[width].keeda}
            onChange={val =>
              handleWidthChange(record.key, width, 'keeda', val)
            }
            onFocus={handleInputFocus}
          />
        </div>
      ),
    })),
    {
      title: 'Total Pcs',
      key: 'totalPcs',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => calculateTotalPcs(record.widths),
    },
    {
      title: 'CFT',
      key: 'cft',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: WoodRow) => {
        const cft = calculateCFT(record.woodId, record.widths);
        return <strong>{cft}</strong>;
      },
    },
    {
      title: 'Rate',
      key: 'rate',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => <span>Rs. {record.rate}</span>,
    },
    {
      title: 'Amount',
      key: 'amount',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: WoodRow) => {
        const cft = calculateCFT(record.woodId, record.widths);
        const amount = calculateAmount(cft, record.rate);
        return <strong>Rs. {amount}</strong>;
      },
    },
  ];

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Self NAPI</Breadcrumb.Item>
        </Breadcrumb>
      </Space>

      <div style={{ padding: 20 }}>
        <h2>Purchase Order Detail</h2>

        <Form layout="vertical">
          <Row gutter={16} align="middle">
            <Col span={16}>
              <span>
                <strong>Company Name:</strong> {billdata?.ledger_company_name}{' '}
                {billdata?.ledger_gstin && `(GSTIN: ${billdata?.ledger_gstin})`}
              </span>
            </Col>
            <Col span={8}>
              <strong>Challan No.:</strong> <span>{billdata?.pbill_challan_no}</span>
            </Col>
          </Row>
        </Form>

        <div ref={tableRef} style={{ overflowX: 'auto', marginTop: 20 }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            bordered
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
          <Button
            style={{ marginLeft: 10 }}
            onClick={() => navigate('/purchase-order?type=timber')}
          >
            Skip
          </Button>
        </div>
      </div>
    </>
  );
};

export default PartyNapi;

